import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import { postTagSuccess } from "store/actions/tagsList";

import { updateTag } from "api/tagsList";

import getLogger from "utils/logger.util";
import { extractSystemId } from "utils/utils";
import { DROPDOWN_STRINGS, InputErrorStrings, TagPageStrings, valueStrings } from "constants/constantStrings";
import { API_STATUS_STRINGS } from "constants/constantStrings";
import { INPUT_MINIMUM } from "constants/globalConstants";

import DupontDialog from "components/DupontDialog";
import InputBox from "components/InputBox";
import createNotification, { SUCCESS_NOTIFICATION } from "components/Notifications";

export default function TagListEditForm(props) {
  const dispatch = useDispatch();
  const { systemsAsObj } = useSelector(state => state.systems);

  const { selectedTagDetails, editTagFormCloseHandler } = props;
  const [formData, setFormData] = useState(selectedTagDetails);
  const [confirmDisabled, setConfirmDisabled] = useState(true);
  const [isLoading, setIsLoading] = useState(false);

  const systemID = extractSystemId();
  const { systemName } = systemsAsObj[systemID];
  const [tagDescriptionError, setTagDescriptionError] = useState(null);
  const [tagNameError, setTagNameError] = useState(null);
  const Logger = getLogger("TagListEditForm");

  const isFieldInvalid = (srcData, field) => {
    const fieldVal = srcData[field];
    let isInvalid = true;
    if (fieldVal) {
      isInvalid = fieldVal.trim().length < INPUT_MINIMUM;
    }
    return isInvalid;
  };

  const onInputChange = (value, name) => {
    const updatedData = { ...formData, [name]: value };

    const isTagNameInValid = isFieldInvalid(updatedData, "TagName");
    const isDescritptionInValid = isFieldInvalid(updatedData, "Description");

    const isDisabled = isTagNameInValid || isDescritptionInValid || !updatedData.Units || updatedData.Units.trim().length === 0;

    if (isTagNameInValid) {
      setTagNameError(InputErrorStrings.minimumRequired);
    } else {
      setTagNameError(null);
    }

    if (isDescritptionInValid) {
      setTagDescriptionError(InputErrorStrings.minimumRequired);
    } else {
      setTagDescriptionError(null);
    }

    if (!isDisabled) {
      setTagDescriptionError(null);
      setTagNameError(null);
    }
    setFormData(updatedData);
    setConfirmDisabled(isDisabled);
  };

  const onMaxLengthReachedDescription = maxLength => setTagDescriptionError(InputErrorStrings.maxlimitis + maxLength);
  const onMaxLengthReachedTag = maxLength => setTagNameError(InputErrorStrings.maxlimitis + maxLength);

  const onFormSubmit = async data => {
    if (data === "CONFIRM") {
      try {
        setIsLoading(true);
        const { TagId, Value, ...payload } = formData;
        const data = await updateTag(TagId, payload);
        if (data) {
          data.Group = DROPDOWN_STRINGS.standardTags;
          dispatch(postTagSuccess(data));
          createNotification(SUCCESS_NOTIFICATION, API_STATUS_STRINGS.put, "Success");
        }
        editTagFormCloseHandler();
      } catch (err) {
        Logger.error("Error while updating Tags data: ", err);
      } finally {
        setIsLoading(false);
      }
    } else {
      editTagFormCloseHandler();
    }
  };

  return (
    <DupontDialog
      open
      title={TagPageStrings.editTag}
      subTitle={TagPageStrings.editTagDescription.replace("[%]", systemName)}
      showisRequired
      cancelBtnText={valueStrings.cancel}
      confirmBtnText={valueStrings.save}
      onClose={onFormSubmit}
      isOverlayLoader={isLoading}
      confirmDisabled={confirmDisabled}
    >
      <div className="column">
        <InputBox labelText={`${TagPageStrings.tagId}*`} name="TagId" value={formData.TagId} disabled />
        <div className="margin-top-20">
          <InputBox
            labelText={`${TagPageStrings.tagDescription}*`}
            name="Description"
            requiredMsg={InputErrorStrings.required}
            value={formData.Description}
            disabled={formData.TagId.includes(TagPageStrings.customTag)}
            onChange={onInputChange}
            maxLength={500}
            errorMsg={tagDescriptionError}
            onMaxLengthReached={onMaxLengthReachedDescription}
          />
        </div>
      </div>
      <div className="column">
        <InputBox
          labelText={`${TagPageStrings.tagName}*`}
          name="TagName"
          value={formData.TagName}
          requiredMsg={InputErrorStrings.required}
          maxLength={100}
          disabled
          errorMsg={tagNameError}
          onMaxLengthReached={onMaxLengthReachedTag}
        />
        <div className="margin-top-20">
          <InputBox
            labelText={`${TagPageStrings.tagUnit}*`}
            name="Units"
            value={formData.Units}
            requiredMsg={InputErrorStrings.required}
            onChange={onInputChange}
            maxLength={20}
            disabled={formData.TagId.includes(TagPageStrings.customTag)}
          />
        </div>
      </div>
    </DupontDialog>
  );
}
